import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Modal,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEmpty } from "lodash";
import { useNavigate } from "react-router-dom";
import { nanoid } from "nanoid";

import { MyAccountModel } from "../../../models/MyAccount";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  AddUserModalBox,
  AddUserModalGrid,
  FilterByHeaderStyle,
  GridRowStyle,
  HeaderInputFieldWidth,
  MainContainer,
  NoRecordFound,
} from "../style/style";
import {
  flexAllCentre,
  flexAllEnd,
  flexRow,
} from "../../../styles/mui/styles/display";
import { ButtonContained } from "../../../app/common/button";
import {
  ButtonClearStyle,
  ButtonCloseStyle,
  ButtonVerifyStyle,
  ContainerStyle,
  FaXmarkIcon,
  HorizontalDivider,
  ModalGrid,
  PaginationGrid,
  StyledTableHead,
  tableCell,
  tableCellDynamic,
  tableCellDynamic2,
} from "../../../app/styles/styles";
import { useForm } from "react-hook-form";
import colors from "../../../styles/colors/colors";
import { getCurrnetPageFullPath, getUaserPageViewPayload, isAdmin } from "../../../utils";
import {
  AccessAdminListActionDispatchTypes,
  AdminAccessFilterBy,
  AgencyAccessLabel,
  getValidMessageType,
} from "../../../constants/User";
import { FormInputDropdown } from "../../../components/FormInputDropdown";
import { FormInputTextStandard } from "../../../components/FormInputTextStandard";
import {
  AdminAccessListPayload,
  AdminAccessList,
  AdminUserList,
  UpdateAgencyAccessPayload,
  UserPageTrack,
} from "../../../models/User";
import PaginationItem from "../../../app/common/pagination/components/PaginationItem";
import { PER_PAGE } from "../../../constants/FunctionalUtils";
import { searchAdminAccessListPayload } from "../../../constants/Admin";
import { rules } from "../../../utils/validation/Validation";
import { ServiceProvider } from "../../../models/ServiceProvider";
import { Numbers } from "../../../constants/Referral";
import { ApplicationRoute } from "../../../constants/ApplicationUrl";
import {
  MessageType,
  ResponseMessages,
} from "../../../constants/ResponseMessage";
import { maxServiceProviderCount, redirectUri } from "../../../constants/Config";
import { ApplicationTitles } from "../../../constants/HomePage";

export interface PropsFromDispatch {
  getMasterServiceProvider: () => void;
  postAccessAdminList: (payload: AdminAccessListPayload) => void;
  resetStates: (actionType: string) => void;
  updateAgencyAccess: (payload: UpdateAgencyAccessPayload) => void;
  postUserPageTrack: (payload: UserPageTrack) => void;
}

export interface PropsFromState {
  loggedInUser: MyAccountModel;
  masterServiceProviderState: ServiceProvider[];
  userListState: AdminUserList;
  userListLoading: boolean;
  adminAccessListState: AdminAccessList[] | null;
  updateAgencyAccessState: any
}

export interface FormData {
  email: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  mfaId: number;
}

export interface Data {
  filterBy: string;
  search: string;
}

type AllProps = PropsFromState & PropsFromDispatch;

const AdminPortal: React.FC<AllProps> = ({
  loggedInUser,
  postAccessAdminList,
  resetStates,
  adminAccessListState,
  updateAgencyAccess,
  updateAgencyAccessState,
  postUserPageTrack
}) => {
  const navigate = useNavigate();
  const methods = useForm<any>({
    defaultValues: "",
  });
  const [disabled, setDisabled] = useState<boolean>(true);
  const [disabledSearchInput, setDisabledSearchInput] = useState<boolean>(true);
  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    watch,
    clearErrors,
  } = methods;
  const [modalFlag, setModalFlag] = useState<boolean>(false);
  const [allowPa, setAllowPa] = useState<boolean>(false);
  const [btnType, setBtnType] = useState<number>(0);
  const [pageChangeNumber, setPageChangeNumber] = useState<number>(0);
  const [message, setMessage] = useState<string>("");
  const [pageNumber, setPageNumber] = useState(1);
  const count = Math.ceil(
    adminAccessListState && adminAccessListState[0] !== undefined && adminAccessListState[0].totalRows
      ? Number(adminAccessListState[0].totalRows) / PER_PAGE
      : 1 / PER_PAGE
  );
  

  useEffect(() => {
    const subscription = watch((_value: any, { name }: any) => {
      if (
        name === "search" &&
        !isEmpty(_value.search) &&
        _value.search.length > 0
      ) {
        setDisabled(false);
      }
      if (name === "filterBy" && !isEmpty(_value.filterBy)) {
        setDisabledSearchInput(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const searchCriteria = (value: any, pageFlag: boolean, page: number, isAllowPa?: boolean) => {
    resetStates(AccessAdminListActionDispatchTypes.ACCESS_ADMIN_RESET);
    setPageNumber(page);

    let isPriorAuthEnableValue = null;
    if (allowPa) {
      isPriorAuthEnableValue = isAllowPa;
    } else if (isAllowPa) {
      isPriorAuthEnableValue = allowPa;
    }

    const temp = {
      ...searchAdminAccessListPayload,
      [value.filterBy]: value.search,
      pageNo: pageFlag ? page : 1,
      isPriorAuthEnable: isPriorAuthEnableValue,
      maxServiceProviderCount: maxServiceProviderCount,
    };

    return temp;
  };

  const onSubmit = (data: Data) => {
    postAccessAdminList(searchCriteria(data, false, 1, allowPa));
  };

  useEffect(() => { 
    if (updateAgencyAccessState && !isEmpty(updateAgencyAccessState?.message)) {
      postAccessAdminList(
        searchCriteria(
          {
            filterBy: getValues("filterBy"),
            search: getValues("search"),
          },
          true,
          1,
          allowPa
        )
      );
    }
  }, [updateAgencyAccessState]);

  const handlePageChange = (_e: ChangeEvent<unknown>, page: number) => {
    setMessage(ResponseMessages(MessageType.WARNING_PAGE_CHANGE));
    setPageChangeNumber(page);
    setBtnType(Numbers.TWO);
  };

  useEffect(() => {
    if (getValues("filterBy") === null || isEmpty(getValues("filterBy"))) {
      reset();
      setDisabled(true);
      setDisabledSearchInput(true);
    } else {
      setDisabled(false);
      setDisabledSearchInput(false);
    }
  }, [getValues("filterBy")]);

  const handleSearch = () => {
    handleSubmit(onSubmit)();
  };

  const handleClear = () => {
    setValue("filterBy", null);
    setValue("search", "");
    setAllowPa(false);
    setSelectAll(false);
    reset();
    postAccessAdminList(searchCriteria("default", false, Numbers.ONE, false));
    setSelectedCheckBox([]);
  };

  useEffect(() => {
    reset({ search: "", filterBy: null });
    clearErrors("search");
    postAccessAdminList(searchCriteria("default", false, Numbers.ONE));
    postUserPageTrack(
      getUaserPageViewPayload(
        getValidMessageType(ApplicationTitles.AGENCY_ACCESS),
        getCurrnetPageFullPath(),
        redirectUri,
        ""
      )
    );
  }, []);

  const handleChange = (item: any, value: boolean) => {
    setSelectedCheckBox(getUpdatedArray(item, value));
  };

  const getUpdatedArray = (item: any, value: boolean) => {
    if (item) {
      const exists = selectedCheckBox.find(
        (prevItem: any) => prevItem.serviceProviderId === item.serviceProviderId
      );
      if (exists) {
        return selectedCheckBox.map((prevItem: any) =>
          prevItem.serviceProviderId === item.serviceProviderId
            ? { ...prevItem, isPriorAuthEnable: value }
            : prevItem
        );
      } else {
        return [...selectedCheckBox, { ...item, isPriorAuthEnable: value }];
      }
    } else {
      return selectedCheckBox.map((prevItem: any) =>
        prevItem.serviceProviderId === item.serviceProviderId
          ? { ...prevItem, isPriorAuthEnable: value }
          : prevItem
      );
    }
  };

  const defaultGetAgencyList = () => {
    setSelectedCheckBox([]);
    if (
      adminAccessListState &&
      adminAccessListState.length > Numbers.ZERO
    ) {
      adminAccessListState.forEach((item) =>
        setSelectedCheckBox((prev: any) => [
          ...prev,
          {
            ...item,
            isPriorAuthEnable: item.isPriorAuthEnable
              ? item.isPriorAuthEnable
              : false,
          },
        ])
      );
    }
  }

  useEffect(() => {
    defaultGetAgencyList();
  }, [adminAccessListState]);

  useEffect(() => {
    setSelectedCheckBox([]);
    if (
      adminAccessListState &&
      adminAccessListState.length > Numbers.ZERO
    ) {
      adminAccessListState.forEach((item) =>
        setSelectedCheckBox((prev: any) => [
          ...prev,
          selectAll ? {
            ...item,
            isPriorAuthEnable: true
          } :  {
            ...item,
            isPriorAuthEnable: false
          },
        ])
      );
    }
  }, [selectAll])
  
  useEffect(() => {
      postAccessAdminList(
        searchCriteria(
          {
            filterBy: getValues("filterBy"),
            search: getValues("search"),
          },
          true,
          1,
          allowPa
        )
      );
  },[allowPa])
  const label = { inputProps: { 'aria-label': 'Size switch demo' } };
  const addButtons = () => {
    return isAdmin(loggedInUser) ? (
      <Grid container spacing={2}>
        <ButtonContained
          id="btnSearch"
          onClick={() => handleSearch()}
          label="Search"
          disabled={disabled}
          style={ButtonVerifyStyle}
        />
        <ButtonContained
          id="btnClear"
          onClick={() => handleClear()}
          label="Clear"
          style={ButtonClearStyle}
        />
        <Grid container>
          <Grid
            sx={GridRowStyle}
          >
            <Grid size={{ xs: 8 }} pt={0.7}>
              <Typography
                variant="subtitle2"
                fontWeight={fontWeight[4]}
                color={colors.fonts[2]}
              >
                Allow PA : 
              </Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <FormControlLabel
                onChange={(_e, value) => {
                  setAllowPa(value)
                }
                }
                checked={allowPa}
                control={<Switch {...label} size="medium" color="primary" />}
                label=""
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    ) : null;
  };

  const cellRender = (
    label: any,
    flag: boolean,
    removeBorder: boolean,
    item: AdminAccessList
  ) => {
    return (
      <>
        <TableCell
          sx={
            flag === false
              ? tableCellDynamic(flag, removeBorder)
              : tableCellDynamic2(flag, removeBorder)
          }
        >
          {label === "check" ? (
            <Checkbox
              onChange={(_e, value) => handleChange(item, value)}
              sx={{ padding: 0 }}
              value={item.isPriorAuthEnable}
              checked={
                selectAll ? true : item.isPriorAuthEnable !== null && item.isPriorAuthEnable
              }
              size="medium"
            />
          ) : (
            <Typography
              variant="subtitle1"
              fontWeight={fontWeight[5]}
              color={colors.fonts[4]}
            >
              {label}
            </Typography>
          )}
        </TableCell>
      </>
    );
  };
  const rowRender = (data: AdminAccessList[]) => {
    return (
      <>
        {data && data.length && data[0].serviceProviderId > 0
          ? data.map((item: AdminAccessList, index: number) => (
              <>
                <TableRow key={nanoid()}>
                  {cellRender("check", false, data.length - 1 === index, item)}
                  {cellRender(
                    item.providerNpi,
                    false,
                    data.length - 1 === index,
                    item
                  )}
                  {cellRender(
                    item.providerName,
                    false,
                    data.length - 1 === index,
                    item
                  )}
                  {cellRender(
                    item.streetName1,
                    false,
                    data.length - 1 === index,
                    item
                  )}
                  {cellRender(
                    item.streetName2,
                    false,
                    data.length - 1 === index,
                    item
                  )}
                  {cellRender(
                    item.providerCity,
                    false,
                    data.length - 1 === index,
                    item
                  )}
                  {cellRender(
                    item.providerState,
                    false,
                    data.length - 1 === index,
                    item
                  )}
                  {cellRender(
                    item.zipCode,
                    false,
                    data.length - 1 === index,
                    item
                  )}
                </TableRow>
              </>
            ))
          : NoRecordFound("0")}
      </>
    );
  };


  useEffect(() => {
    if (btnType > 0) {
      setModalFlag(true);
    }
  }, [btnType]);

  useEffect(() => {
    setSelectAll(false);
  },[pageNumber])

  const onSubmitModal = () => {
    setSelectAll(false);
    setBtnType(0);
    setSelectedCheckBox([]);
    if (btnType === Numbers.ONE) {
      navigate(ApplicationRoute.ADMIN);
    }
    if (btnType === Numbers.TWO) {
      setPageNumber(pageChangeNumber);
      postAccessAdminList(
        searchCriteria(
          {
            filterBy: getValues("filterBy"),
            search: getValues("search"),
          },
          true,
          pageChangeNumber,
          allowPa
        )
      );
    }
    setModalFlag(false);
  };

  const onCancel = () => {
    setModalFlag(false);
    setBtnType(0);
  };

  const onUpdateAgencySubmit = () => {
    const payload = {
      userCreated: loggedInUser?.providerPortalUser?.email,
      selectedServiceProviderList: selectedCheckBox,
    };
    if (selectedCheckBox && selectedCheckBox.length > 0) {
      updateAgencyAccess(payload);
    }
  };

  return (
    <Box>
      <Grid container>
        <Grid size={{ xs: 12 }} mt={2} mb={1} sx={flexRow}>
          <Grid container sx={FilterByHeaderStyle} ml={2}>
            <FormControl
              variant="standard"
              fullWidth
              sx={HeaderInputFieldWidth}
            >
              <FormInputDropdown
                name="filterBy"
                control={control}
                label="Filter By :"
                list={AdminAccessFilterBy}
                readOnly={false}
                onChangeHandler={() => {
                  setValue("search", "");
                }}
              />
            </FormControl>
            <FormControl
              variant="standard"
              fullWidth
              sx={HeaderInputFieldWidth}
            >
              <FormInputTextStandard
                name="search"
                control={control}
                label="Search :"
                textLength={50}
                helper={rules.search}
                readOnly={disabledSearchInput}
              />
            </FormControl>
            <Grid pt={1.8}>{addButtons()}</Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={HorizontalDivider} />
        </Grid>
        <Grid size={{ xs: 12 }} sx={MainContainer}>
          <Grid size={{ xs: 12 }} pl={"1rem"} pb={"1rem"}>
            <TableContainer component={Paper} sx={ContainerStyle}>
              <Table stickyHeader>
                <StyledTableHead>
                  <TableRow>
                    {AgencyAccessLabel.map((data: any) => (
                      <TableCell key={data.label} sx={tableCell}>
                        <Box sx={data.style?.cell}>
                          <Typography
                            color={colors.black[16]}
                            variant="body1"
                            fontWeight={fontWeight[3]}
                          >
                            {data.label}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </StyledTableHead>
                <TableRow>
                    {AgencyAccessLabel.map((data: any) => (
                      <TableCell key={data.label}>
                       {data.label === "Allow PA" && !allowPa && (
                          <Grid container sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Grid size={{ xs: 12 }} pb={1}>
                              <Typography
                                color={colors.black[16]}
                                variant="body1"
                                fontWeight={fontWeight[3]}
                              >
                              Select All
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 2 }}>
                              <Checkbox
                                disabled={allowPa}
                                onChange={(_e, value) => setSelectAll(value)}
                                sx={{ padding: 0 }}
                                checked={selectAll}
                                size="medium"
                              />
                            </Grid>
                          </Grid>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                <TableBody>
                  {rowRender(selectedCheckBox ? selectedCheckBox : [])}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }} pt={1} pr={7} sx={flexAllEnd}>
          <ButtonContained
            id="btnSubmitAdminAccess"
            onClick={() => onUpdateAgencySubmit()}
            label="Submit"
            style={ButtonVerifyStyle}
          />
          <ButtonContained
            id="btnCloseAddUser"
            onClick={() => {
              setBtnType(1);
              setMessage(ResponseMessages(MessageType.WARNING_CANCEL));
            }}
            label="Cancel"
            style={ButtonCloseStyle}
          />
        </Grid>
      </Grid>
      <Grid size={{ xs: 12 }} pr={6} sx={PaginationGrid}>
        <PaginationItem
          count={count}
          page={pageNumber}
          handlePageChange={handlePageChange}
        />
      </Grid>
      {modalFlag && (
        <Modal
          open={modalFlag}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={flexAllCentre}
        >
          <Box sx={AddUserModalBox(true)}>
            <Grid container mb="0.4rem">
              <Grid size={{ xs: 11.5 }}>
                <Typography
                  id="modal-modal-title"
                  fontWeight={fontWeight[6]}
                  variant="h1"
                >
                  Confirmation needed
                </Typography>
              </Grid>
              <Grid size={{ xs: 0.5 }}>
                <IconButton onClick={() => onCancel()}>
                  <FaXmarkIcon />
                </IconButton>
              </Grid>
            </Grid>
            <Divider sx={HorizontalDivider} />
            <Box sx={ModalGrid}>
              <Typography
                variant="h2"
                fontWeight={fontWeight[4]}
                sx={{ color: colors.fonts[6] }}
              >
                {message}
              </Typography>
            </Box>
            <Divider sx={HorizontalDivider} />
            <Grid container sx={AddUserModalGrid}>
              <Grid size={{ xs: 12 }} sx={flexAllEnd}>
                <ButtonContained
                  id="btnSubmitAddUser"
                  onClick={() => onSubmitModal()}
                  label="Yes"
                  disabled={false}
                  style={ButtonVerifyStyle}
                />
                <ButtonContained
                  id="btnCloseAddUser"
                  onClick={() => onCancel()}
                  label="No"
                  disabled={false}
                  style={ButtonCloseStyle}
                />
              </Grid>
            </Grid>
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default AdminPortal;
